import axiosClient from './axiosClient';

/**
 * Nhập hoá đơn bằng AI — upload ảnh/PDF, xem AI đọc ra gì, sửa, rồi ghi vào
 * MeInvoice.
 *
 * Đặt ở driving-dat vì toàn bộ luồng hoá đơn đã ở đây (MeInvoicePage,
 * MeInvoiceBatchPage): kế toán duyệt xong là quản lý tiếp chính hoá đơn đó,
 * không phải nhảy sang app khác.
 *
 * Việc đọc chạy ở worker nền nên `createBatch` trả về gần như ngay lập tức và
 * trang phải poll `getBatch` cho tới khi status rời khỏi 'extracting'. Một lô
 * 92 file mất khoảng 5 phút — giữ một request HTTP mở lâu như vậy thì proxy
 * cắt trước khi xong.
 */

const API = '/meinvoice/ocr';

/**
 * @param {File[]} files
 * @param {{ note?: string, onUploadProgress?: Function }} opts
 */
const createBatch = (files, { note = '', onUploadProgress } = {}) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  if (note) fd.append('note', note);
  // Không set Content-Type thủ công — axios tự thêm boundary cho FormData.
  return axiosClient.post(`${API}/batch`, fd, { onUploadProgress });
};

const listBatches = (params) => axiosClient.get(`${API}/batch`, { params });

/** Trả về `{ batch, items }`. */
const getBatch = (id) => axiosClient.get(`${API}/batch/${id}`);

const updateItem = (id, data) => axiosClient.patch(`${API}/item/${id}`, data);

/**
 * Ghi vào MeInvoice.
 *
 * `blockBatching` mặc định TRUE: hoá đơn ghi xong vẫn bị chặn khỏi lệnh chi
 * tiền cho giáo viên cho tới khi có người chủ động mở. Truyền `false` là một
 * hành động có ý thức — sai hướng đó là tiền đã chi.
 */
const commitBatch = (id, { blockBatching = true, itemIds = null } = {}) =>
  axiosClient.post(`${API}/batch/${id}/commit`, { blockBatching, itemIds });

/** Đọc lại các dòng lỗi. */
const retryBatch = (id) => axiosClient.post(`${API}/batch/${id}/retry`);

const deleteBatch = (id) => axiosClient.delete(`${API}/batch/${id}`);

/**
 * "Đối chiếu lại" một đợt với MISA: kéo khoảng ngày của đợt từ MISA về rồi
 * khớp lại từng dòng. Chạy nền — trả 202, tiến độ nằm ở `batch.doiChieu`.
 */
const doiChieuLaiMisa = (id) => axiosClient.post(`${API}/batch/${id}/doi-chieu-misa`);

/**
 * Đối chiếu bảng kê .xlsx của kế toán với hoá đơn trong hệ thống.
 *
 * CHỈ ĐỌC — không có hàm tạo hoá đơn từ bảng kê, và đó là chủ ý. Bảng kê
 * chỉ có số hoá đơn, ngày, người bán và tổng tiền: không mã số thuế, không ký
 * hiệu, nên không dựng được khoá chống trùng. Đo trên file mẫu thật: 17/21 dòng đã
 * có sẵn với đầy đủ định danh, nên nhập từ bảng kê là đẻ ra 17 bản nghèo hơn.
 *
 * @param {File} file
 * @returns {Promise} { sheetName, headerRow, rows[], tong: { khop, mo, thieu, lech } }
 */
const doiChieuXlsx = (file, { onUploadProgress } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  return axiosClient.post(`${API}/xlsx/doi-chieu`, fd, { onUploadProgress });
};

/**
 * Tạo các hoá đơn bảng kê CÓ mà hệ thống CHƯA có.
 *
 * Chỉ gọi được khi `doiChieuXlsx` trả về `hasInvoiceIdentity === true`, tức file có
 * đủ cột Ký hiệu và Mã số thuế người bán. Thiếu chúng thì khoá chống trùng sai,
 * và khi nhà cung cấp đồng bộ bản thật về sẽ thành hai bản ghi cho một hoá đơn.
 *
 * `blockBatching` mặc định TRUE như luồng ảnh: hoá đơn tạo xong vẫn bị chặn khỏi
 * lệnh chi tiền cho tới khi có người chủ động mở.
 */
const taoThieuXlsx = (file, { blockBatching = true } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('blockBatching', String(blockBatching));
  return axiosClient.post(`${API}/xlsx/tao-thieu`, fd);
};

const invoiceOcrService = {
  createBatch, listBatches, getBatch, updateItem, commitBatch, retryBatch, deleteBatch, doiChieuLaiMisa,
  doiChieuXlsx, taoThieuXlsx,
};

export default invoiceOcrService;
